import { IconHelperProps } from '../../generic/IconHelper';
import type { RecentStoryTemplate } from 'components/editorial/AssetQuickLaunch/useRecentStoryTemplates';

export interface IViewState {
  view: 'main' | 'component';
  viewComponentKey: string | null;
}

// Pinned story templates share the same ordered pin array as apps (LS key
// `createPinnedApps`). To tell the two apart, template pins are keyed with this
// prefix followed by the template uuid (e.g. "template-<uuid>"). The matching
// snapshot ({uuid,title,type}) is stored separately under `createPinnedTemplates`
// so a pinned template can render even when it isn't in the recent/fetched set.
export const PINNED_TEMPLATE_PREFIX = 'template-';
export const makeTemplateKey = (uuid: string): string =>
  `${PINNED_TEMPLATE_PREFIX}${uuid}`;
export const isTemplateKey = (key: string): boolean =>
  key.startsWith(PINNED_TEMPLATE_PREFIX);

// A pinned template's stored snapshot. `site` records which site it was pinned
// on: templates are site-specific assets, so we only surface pins matching the
// active site. Without this, switching sites would leave another site's template
// pinned and clicking it would create-from-template into an "asset not found".
export type PinnedTemplate = RecentStoryTemplate & { site: string };

export interface ISearchState {
  query: string;
  showAll: boolean;
}

// stable unique react key and also appended sometimes as id/testid
// in some cases eg "plus-button-search-item-editorial-article"
// additionally used in local storage keys, as the order is stored in an array (LS Key: createPinnedApps)
export type PlusNavObj = {
  key: string; // stable unique key for the item
  label: string; // Text label for the item.
  path?: string; // Where this links to... interacts with action prop.
  action: 'navigate' | 'display' | 'callback' | 'template'; // Action type to handle
  icon: React.ComponentType<IconHelperProps | any>; // Icon component
  keywords: string[]; // Keywords for search matching
  showTitle?: boolean; // Set to false to hide the title.
  disabled?: boolean; // Set true to disable if needed.
  hidden?: boolean; // Set true to hide if needed.
  component?: React.ComponentType<any>; // Component to show if action is "display"
  template?: RecentStoryTemplate; // Source template when action is "template"
};

export type PlusMenuRecord = {
  editorial: {
    metadata: {
      header: string;
    };
    items: {
      article: PlusNavObj;
      image: PlusNavObj;
      collection: PlusNavObj;
      audio: PlusNavObj;
      file: PlusNavObj;
      html: PlusNavObj;
      link: PlusNavObj;
      poll: PlusNavObj;
      table: PlusNavObj;
      video: PlusNavObj;
    };
  };
  user: {
    metadata: {
      header: string;
    };
    items: {
      create: PlusNavObj;
    };
  };
  notifier: {
    metadata: {
      header: string;
    };
    items: {
      create_post: PlusNavObj;
    };
  };
  batch: {
    metadata: {
      header: string;
    };
    items: {
      batch: PlusNavObj;
    };
  };
  blackbird: {
    metadata: {
      header: string;
    };
    items: {
      blackbird: PlusNavObj;
    };
  };
};

// eg: 'editorial' | 'user' | 'batch';
export type PlusMenuKey = keyof PlusMenuRecord;
